import React, { useEffect } from 'react';
import { ThemeProvider, createTheme, CssBaseline, Box, Alert, Button } from '@mui/material';
import { AppProvider } from './context/AppContext';
import { ViewProvider } from './context/ViewContext';
import { EnergyProvider, useEnergyContext } from './context/EnergyContext';
import MainLayout from './components/MainLayout';
import RealTimeIndicator from './components/RealTimeIndicator';
import { AuthProvider, useAuth } from './auth/AuthContext';
import LoginPage from './auth/LoginPage';
import DemoGuestEntry from './auth/DemoGuestEntry';

import { useAppContext } from './context/AppContext';
import './App.css';

// Create a theme instance
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2', // Blue
    },
    secondary: {
      main: '#dc004e', // Pink
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: [
      'Roboto',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
});

/**
 * Main App component that serves as the container for the SOMEAC application.
 * Uses both AppContext for customer/fleet state and ViewContext for view state.
 * Features real-time service initialization and global status monitoring.
 */
function AppContent() {
  const energy = useEnergyContext();
  const connectionStatus = { connectionStatus: energy?.errors?.some(error => error.source === 'connection' || error.source === 'session') ? 'error' : 'connected' };
  const auth = useAuth();
  const { customerContext, setCustomer, clearCustomer } = useAppContext();
  const previousIdentity = React.useRef(null);


  // Every non-superuser is auto-scoped to the customer assigned to their login
  // identity. They never receive a customer selector in the UI.
  useEffect(() => {
    const identity = auth.isAuthenticated ? (auth.user?.userId || auth.user?.email) : null;
    const identityChanged = previousIdentity.current !== identity;
    previousIdentity.current = identity;
    if (!auth.isAuthenticated || auth.isSuperuser || !auth.customerId) {
      if (identityChanged || (!auth.isSuperuser && customerContext)) clearCustomer();
      return;
    }
    if (customerContext?.customerId === auth.customerId) return;
    setCustomer({ customerId: auth.customerId, customerName: auth.customerId });
  }, [auth.isAuthenticated, auth.isSuperuser, auth.customerId, auth.user?.userId, auth.user?.email, customerContext, setCustomer, clearCustomer]);

  if (!auth.isAuthenticated) {
    return <><LoginPage /><DemoGuestEntry /></>;
  }

  if (!auth.isSuperuser && !auth.customerId) {
    return <Alert severity="warning" action={<Button onClick={auth.signOut}>Sign out</Button>}>Your account has no assigned system. Contact an administrator.</Alert>;
  }
  if (!auth.isSuperuser && customerContext?.customerId !== auth.customerId) {
    return <Box sx={{ p: 3 }}>Loading your account…</Box>;
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {auth.user?.isDemoGuest && <Alert severity="info" icon={false} sx={{ borderRadius: 0, py: 0.25 }}>Demonstration system · Read-only browsing</Alert>}
      {/* Global real-time status bar */}
      {(connectionStatus.connectionStatus === 'error' || connectionStatus.connectionStatus === 'fallback') && (
        <Box sx={{ 
          p: 1, 
          backgroundColor: connectionStatus.connectionStatus === 'error' ? '#fff3cd' : '#d1ecf1',
          borderBottom: '1px solid',
          borderColor: connectionStatus.connectionStatus === 'error' ? '#ffeaa7' : '#bee5eb',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <RealTimeIndicator connectionStatus={connectionStatus} isRealTime />
          <Alert 
            severity={connectionStatus.connectionStatus === 'error' ? 'warning' : 'info'}
            sx={{ 
              flex: 1, 
              py: 0,
              '& .MuiAlert-message': { py: 0.5 }
            }}
          >
            {connectionStatus.connectionStatus === 'error' 
              ? 'Real-time connection error. Some features may be limited.' 
              : 'Reconnecting energy data.'
            }
          </Alert>
        </Box>
      )}
      
      {/* Main application content */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <MainLayout />
      </Box>
    </Box>
  );
}

/**
 * App wrapper with context provider
 */
function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AppProvider>
          <ViewProvider>
            <EnergyProvider>
              <AppContent />
            </EnergyProvider>
          </ViewProvider>
        </AppProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
